import React, { useState } from "react";
import {
    Box,
    Flex,
    Grid,
    GridItem,
    Text,
    HStack,
} from "@chakra-ui/react";
import {
    ResponsiveContainer,
    PieChart as BasePieChart,
    Pie,
    Cell,
    Tooltip,
} from "recharts";
import { DashboardResponse } from "../types";
import PercentageChange from "../../reports/pages/components/EnergyReportComponents/PercentageChange";
import { getLocationsPercentage, getReadableValue, measurementTypeMap, parameterColumnMap, parameterTitleMap } from "../../../utils/helpers";
import { getShadeFromPercent } from "../../../utils/colors";

const baseColorMap: Record<string, string> = {
    totalconsumedenergy: "#A4D8FD",
    totalgeneratedenergy: "#ADEED0",
    temperature: "#CECFD0",
}

export const getChartColor = (parameterTitle: string): string => {
    const baseColor = baseColorMap[parameterTitle.toLowerCase()]
    if (baseColor) return baseColor

    const hue = Math.floor(Math.random() * 360)
    return `hsl(${hue}, 70%, 80%)`
}

export const getShadesForParameter = (parameterTitle: string, count: number): string[] => {
    const baseColor = getChartColor(parameterTitle)
    return getShades(baseColor, count)
}

const getShades = (baseHex: string, count: number): string[] => {
    if (count === 1) return [baseHex]

    return Array.from({ length: count }, (_, i) => {
        const percent = (i / (count - 1)) * 100
        return getShadeFromPercent(baseHex, percent)
    }).reverse()
}


interface PieChartItemProps {
    record: DashboardResponse;
}

export function PieChartItem({ record }: PieChartItemProps) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const selectedPalette = getShadesForParameter(record.parameter_title, record.values.length);

    const chartData = record.values
        .map((entry, idx) => ({
            name: entry.label,
            value: entry.value,
            cost: entry.cost,
            color: selectedPalette[idx % selectedPalette.length],
        }))
        .sort((a, b) => b.value - a.value);

    return (
        <Box mb={10}>
            <Text fontWeight={700} color="#484848" fontSize="14px" fontFamily="Inter" mb={6}>
                {getReadableValue(parameterTitleMap, record.parameter_title)}
            </Text>

            <Flex direction={{ base: "column", md: "row" }} gap={10}>
                {/* Left Side: Pie Chart */}
                <Box flex={1} minH="300px" h="100%" display="flex" alignItems="center" justifyContent="center">
                    <Box w="100%" h="350px">
                        <ResponsiveContainer width="100%" height="100%">
                            <BasePieChart onMouseLeave={() => setActiveIndex(null)}>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    outerRadius="90%"
                                    onMouseEnter={(_, i) => setActiveIndex(i)}
                                >
                                    {chartData.map((entry, i) => {
                                        const isFocused =
                                            activeIndex === null || activeIndex === i;

                                        return (
                                            <Cell
                                                key={`cell-${i}`}
                                                fill={entry.color}
                                                fillOpacity={isFocused ? 1 : 0.2}
                                            />
                                        );
                                    })}
                                </Pie>
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (
                                            !active ||
                                            activeIndex === null ||
                                            !payload ||
                                            !payload.length
                                        ) {
                                            return null;
                                        }

                                        const item = payload[0].payload;

                                        return (
                                            <Box bg="gray.700" color="white" p={3} borderRadius="md" boxShadow="md">
                                                <Text fontWeight="bold">{item.name}</Text>
                                                <Text fontSize="sm">{item.value.toFixed(2)} {getReadableValue(measurementTypeMap, record.parameter_title)}</Text>
                                                {record.parameter_title !== "temperature" && <Text fontSize="sm">{item.cost.toFixed(2)} лв.</Text>}
                                            </Box>
                                        );
                                    }}
                                />

                            </BasePieChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>

                {/* Right Side: Data Table */}
                <Box flex={1}>
                    <Grid templateColumns="1fr 1fr 1fr" gap={4} fontSize="sm" fontWeight={600} mb={3}>
                        <GridItem color="#9B9B9B">Location</GridItem>
                        <GridItem color="#9B9B9B">{getReadableValue(parameterColumnMap, record.parameter_title)}</GridItem>
                        {record.parameter_title !== "temperature" && <GridItem color="#9B9B9B">Cost</GridItem>}
                    </Grid>

                    {chartData.map((entry, i) => (
                        <Grid
                            key={i}
                            templateColumns="1fr 1fr 1fr"
                            gap={4}
                            alignItems="center"
                            mb={2}
                            fontWeight={500}
                        >
                            <GridItem>
                                <HStack>
                                    <Box w="10px" h="10px" borderRadius="full" bg={entry.color} />
                                    <Text fontWeight={400} fontSize="15px" fontFamily="Inter" color="#484848">{entry.name}</Text>
                                </HStack>
                            </GridItem>
                            <GridItem fontWeight={700} color="#484848" fontSize="15px" fontFamily="Inter">
                                {entry.value.toFixed(2)}
                                <span style={{ marginLeft: "3px", fontWeight: 400, color: "#9B9B9B" }}>{getReadableValue(measurementTypeMap, record.parameter_title)}</span>
                            </GridItem>
                            {record.parameter_title !== "temperature" && <GridItem fontWeight={700} color="#484848" fontSize="15px" fontFamily="Inter">
                                {entry.cost.toFixed(2)} <span style={{ marginLeft: "3px", fontWeight: 400, color: "#9B9B9B" }}>лв.</span>
                            </GridItem>}
                        </Grid>
                    ))}
                    {/* Totals */}
                    <Flex align="center" justify="flex-start" fontSize="sm" mt={8}>
                        <HStack spacing={3}>
                            <Text fontWeight={400} color="#9B9B9B">{record.parameter_title !== "temperature" ? "Total:" : "Average:"}</Text>
                            <Text fontWeight={700} color="#484848">
                                {record.total.toFixed(2)} <span style={{ fontWeight: 400, color: "#9B9B9B" }}>{getReadableValue(measurementTypeMap, record.parameter_title)}</span>
                            </Text>
                            {record.parameter_title !== "temperature" && <Text fontWeight={700} color="#484848">
                                {record.total_cost.toFixed(2)} <span style={{ fontWeight: 400, color: "#9B9B9B" }}>лв.</span>
                            </Text>}
                            <PercentageChange percentageChange={getLocationsPercentage(record.total, record.prev_total)} />
                        </HStack>
                    </Flex>

                </Box>
            </Flex>

            <Box border="5px solid #DEDFE2" opacity="20%" borderRadius="full" mt={4} />
        </Box>
    );
}

interface Props {
    data: DashboardResponse[];
}

export function PieChart({ data }: Props) {
    return (
        <Box width="100%">
            {data.map((record, index) => (
                <PieChartItem key={index} record={record} />
            ))}
        </Box>
    );
}